import {BarEventStatus} from "@uptime-com/uptimebar";
import type {ReactElement} from "react";
import {useCallback, useMemo} from "react";

import {Card} from "@/components/card/Card.tsx";
import {Dot} from "@/components/dot/Dot.tsx";
import {StatusPageTimeFormat, UptimeCalculationType} from "@/lib/types/statuspages.ts";
import {ScrollableUptimeBar} from "@/pages/statuspages/consumption/components/ScrollableUptimeBar/ScrollableUptimeBar.tsx";

import type {ComponentDrilldownResult, PublicComponentStatusInfo, PublicStatusPageComponent} from "../../types.ts";
import {buildServiceComponentMap, generateEventsFromDowntimes} from "../../utils/uptimeBarEvents.ts";
import type {PillToolTipProps} from "../StatusPageStatusBar/StatusPageStatusBar.types.ts";
import {StatusPageUptimeBarTooltip} from "../StatusPageUptimeBarTooltip/StatusPageUptimeBarTooltip.tsx";

import styles from "./DrilldownUptimeBar.module.scss";

interface DrilldownUptimeBarProps {
  drilldown: ComponentDrilldownResult;
  start: Date;
  end: Date;
  timezone: string;
  timeFormat?: StatusPageTimeFormat;
  components: PublicStatusPageComponent[];
  statusMetadata: PublicComponentStatusInfo[];
  isDark?: boolean;
}

export const DrilldownUptimeBar = ({
  drilldown,
  start,
  end,
  timezone,
  timeFormat,
  components,
  statusMetadata,
  isDark = false,
}: DrilldownUptimeBarProps) => {
  // Legacy parity: the check-analysis statechart is driven purely by alerts, so time without a
  // recorded outage is up. Deriving the bar from data coverage instead disagreed with the uptime
  // figure beside it — that is computed from the same alerts, so a check with no outages read as
  // 100% uptime next to a fully grey bar.
  //
  // Legacy also stops its series at the current time. Selecting a range that includes today ends
  // the window at 23:59:59, and the check cannot have reported that yet — rendering it left a
  // stretch of operational filler trailing the bar on every selection.
  const renderedEnd = useMemo(() => {
    const now = new Date();
    return end.getTime() > now.getTime() ? now : end;
  }, [end]);

  const downtimeHistory = useMemo(() => drilldown.downtime_history ?? [], [drilldown.downtime_history]);

  const events = useMemo(
    () => generateEventsFromDowntimes(downtimeHistory, start, renderedEnd),
    [downtimeHistory, start, renderedEnd]
  );

  const serviceComponents = useMemo(() => buildServiceComponentMap(components), [components]);

  const pillTooltipRenderer = useCallback(
    ({periodStart, periodEnd}: PillToolTipProps): ReactElement => (
      <StatusPageUptimeBarTooltip
        periodStart={periodStart}
        periodEnd={periodEnd}
        incidents={[]}
        statusMetadata={statusMetadata}
        downtimeHistory={downtimeHistory}
        uptimeCalculationType={UptimeCalculationType.BY_CHECKS}
        serviceComponents={serviceComponents}
        timezone={timezone}
        timeFormat={timeFormat}
      />
    ),
    [statusMetadata, downtimeHistory, serviceComponents, timezone, timeFormat]
  );

  // Spread rather than inline: HTMLProps has no data-* keys, and the tooltip renders in a portal
  // outside the themed page wrapper, so it needs the theme restated.
  const tooltipThemeProps = {"data-theme": isDark ? "dark" : "light"};

  return (
    <Card variant="white" className={styles.uptimeSection}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>Uptime</span>
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <Dot variant="green" size="legend" />
            Uptime
          </span>
          <span className={styles.legendItem}>
            <Dot variant="red" size="legend" />
            Downtime
          </span>
          <span className={styles.legendItem}>
            <Dot variant="yellow" size="legend" />
            Maintenance
          </span>
          <span className={styles.legendItem}>
            <Dot variant="gray" size="legend" />
            No Data
          </span>
        </div>
      </div>
      <ScrollableUptimeBar
        start={start}
        end={renderedEnd}
        timezone={timezone}
        events={events}
        size="large"
        defaultMissingEventStatus={BarEventStatus.OPERATIONAL}
        toolTipContentProps={{className: styles.tooltipContent, ...tooltipThemeProps}}
        pillToolTip={pillTooltipRenderer}
        hoverPill
        stripedNoData
        enableMobileScroll
      />
    </Card>
  );
};
